import {
  APITokenServiceApi,
  BucketServiceApi,
  CSVServiceApi,
  ProductServiceApi,
  SyncServiceApi,
  ChartServiceApi,
  EffectivenessRuleServiceApi,
  LeadScoreAdjustmentRuleServiceApi,
  ReportServiceApi,
  ScheduledReportServiceApi,
  SessionEffectivenessRuleServiceApi,
  AuthApi,
  ClientServiceApi,
  Configuration,
  LocationServiceApi,
  ProjectsServiceApi,
  UserServiceApi,
  ChannelServiceApi,
  AudienceServiceApi,
  EngagementServiceApi,
  CustomisationApi,
  MediaServiceApi,
  LeadServiceApi,
  AlertServiceApi,
  DashboardServiceApi,
  LeadStatusServiceApi,
  PageviewMetaServiceApi,
  AttributionTagsServiceApi,
  JobServiceApi,
  AnalyticsServiceApi,
  PersonalisationServiceApi,
  LandingDomainServiceApi,
  LandersServiceApi,
  UTMServiceApi,
  SessionServiceApi,
  SubscriptionServiceApi,
  SourceCategoryServiceApi,
  LabelServiceApi,
  OnboardingServiceApi,
  MonitoringServiceApi,
  AiServiceApi,
  LastRunInfoServiceApi,
  OAuthServiceApi,
  SamlServiceApi,
  IncrementalityServiceApi,
} from "@incendium/api";
import { checkLogin, logout, trialExpired } from "Actions";
import { getBaseURL } from "config";
import { getAuthToken } from "Helpers";
import { getDetailsByCode } from "Helpers/errors";
import store from "store";

let refreshTokenPromise: Promise<void> | null = null;
let skipAutoRefresh = false; // Flag to disable auto-refresh during initial login

const getRefreshTokenPromise = () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = (
      store.dispatch(checkLogin()) as unknown as Promise<void>
    ).finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

// Helper to temporarily disable auto-refresh (for testing backend timing issues)
export const setSkipAutoRefresh = (skip: boolean) => {
  skipAutoRefresh = skip;
};

let trialExpiredRedirecting = false;

const maybeRedirectTrialExpired = async (response: Response) => {
  if (trialExpiredRedirecting) {
    return;
  }

  if (response.status !== 403) {
    return;
  }

  if (window.location.pathname.includes("/account/payment")) {
    return;
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return;
  }

  const data = await response
    .clone()
    .json()
    .catch(() => null);

  const details = data?.details;
  const isTrialExpired = !!getDetailsByCode(details, "ErrorCodeTrialExpired");
  if (!isTrialExpired) {
    return;
  }

  trialExpiredRedirecting = true;
  store.dispatch(trialExpired());
  setTimeout(() => {
    trialExpiredRedirecting = false;
  }, 1000);
};

const customFetchApi = async (req: RequestInfo, init?: RequestInit) => {
  let tokens = getAuthToken();
  const headers = { ...(init?.headers || {}) };
  if (tokens) {
    headers["Authorization"] = "Bearer " + tokens.token;
  }

  let response = await fetch(req, { ...init, headers });

  if (response.status === 401 && !skipAutoRefresh) {
    // Wait for the ongoing refresh process
    await getRefreshTokenPromise();
    tokens = getAuthToken();
    if (tokens) {
      headers["Authorization"] = "Bearer " + tokens.token;
    }
    // Retry the original request with the updated token
    response = await fetch(req, { ...init, headers });
  }

  maybeRedirectTrialExpired(response).catch(() => null);
  return response;
};

const createConfig = (basePath?: string) => {
  return new Configuration({
    basePath: basePath || getBaseURL(),
    middleware: [
      {
        post: async (res) => {
          // Log out if refresh fails
          if (
            res.response.status === 401 &&
            res.url.includes("api/refresh-token")
          ) {
            store.dispatch(logout());
          }
        },
      },
    ],
    fetchApi: customFetchApi,
  });
};

// API Service instances - will be reinitialized when environment changes
let currentConfig = createConfig();

/**
 * Reinitialize all API services with the current environment's base URL
 * This should be called after login when the environment is selected
 */
export const reinitializeApiServices = () => {
  currentConfig = createConfig();

  // Update all service instances with new config
  Object.assign(userService, new UserServiceApi(currentConfig));
  Object.assign(clientService, new ClientServiceApi(currentConfig));
  Object.assign(projectService, new ProjectsServiceApi(currentConfig));
  Object.assign(authService, new AuthApi(currentConfig));
  Object.assign(locationService, new LocationServiceApi(currentConfig));
  Object.assign(channelService, new ChannelServiceApi(currentConfig));
  Object.assign(trafficBucketService, new BucketServiceApi(currentConfig));
  Object.assign(audienceService, new AudienceServiceApi(currentConfig));
  Object.assign(engagementService, new EngagementServiceApi(currentConfig));
  Object.assign(
    effectivenessRuleService,
    new EffectivenessRuleServiceApi(currentConfig),
  );
  Object.assign(
    sessionEffectivenessRuleService,
    new SessionEffectivenessRuleServiceApi(currentConfig),
  );
  Object.assign(mediaService, new MediaServiceApi(currentConfig));
  Object.assign(leadService, new LeadServiceApi(currentConfig));
  Object.assign(alertService, new AlertServiceApi(currentConfig));
  Object.assign(dashboardService, new DashboardServiceApi(currentConfig));
  Object.assign(leadStatusService, new LeadStatusServiceApi(currentConfig));
  Object.assign(chartService, new ChartServiceApi(currentConfig));
  Object.assign(reportService, new ReportServiceApi(currentConfig));
  Object.assign(
    scheduledReportService,
    new ScheduledReportServiceApi(currentConfig),
  );
  Object.assign(
    leadScoreAdjustmentService,
    new LeadScoreAdjustmentRuleServiceApi(currentConfig),
  );
  Object.assign(APITokenService, new APITokenServiceApi(currentConfig));
  Object.assign(CSVService, new CSVServiceApi(currentConfig));
  Object.assign(productService, new ProductServiceApi(currentConfig));
  Object.assign(syncService, new SyncServiceApi(currentConfig));
  Object.assign(pageviewMetaService, new PageviewMetaServiceApi(currentConfig));
  Object.assign(
    attributionTagService,
    new AttributionTagsServiceApi(currentConfig),
  );
  Object.assign(jobService, new JobServiceApi(currentConfig));
  Object.assign(analyticsService, new AnalyticsServiceApi(currentConfig));
  Object.assign(
    personalisationService,
    new PersonalisationServiceApi(currentConfig),
  );
  Object.assign(
    landingDomainService,
    new LandingDomainServiceApi(currentConfig),
  );
  Object.assign(landersService, new LandersServiceApi(currentConfig));
  Object.assign(utmService, new UTMServiceApi(currentConfig));
  Object.assign(sessionService, new SessionServiceApi(currentConfig));
  Object.assign(subscriptionService, new SubscriptionServiceApi(currentConfig));
  Object.assign(
    sourceCategoryService,
    new SourceCategoryServiceApi(currentConfig),
  );
  Object.assign(analyticsLabelService, new LabelServiceApi(currentConfig));
  Object.assign(onboardingService, new OnboardingServiceApi(currentConfig));
  Object.assign(monitoringService, new MonitoringServiceApi(currentConfig));
  Object.assign(aiService, new AiServiceApi(currentConfig));
  Object.assign(customisationService, new CustomisationApi(currentConfig));
  Object.assign(lastRunInfoService, new LastRunInfoServiceApi(currentConfig));
  Object.assign(oAuthService, new OAuthServiceApi(currentConfig));
  Object.assign(
    incrementalityService,
    new IncrementalityServiceApi(currentConfig),
  );
};

// Initialize API services with initial config
export const userService = new UserServiceApi(currentConfig);
export const clientService = new ClientServiceApi(currentConfig);
export const projectService = new ProjectsServiceApi(currentConfig);
export const authService = new AuthApi(currentConfig);
export const locationService = new LocationServiceApi(currentConfig);
export const channelService = new ChannelServiceApi(currentConfig);
export const trafficBucketService = new BucketServiceApi(currentConfig);
export const audienceService = new AudienceServiceApi(currentConfig);
export const engagementService = new EngagementServiceApi(currentConfig);
export const effectivenessRuleService = new EffectivenessRuleServiceApi(
  currentConfig,
);
export const sessionEffectivenessRuleService =
  new SessionEffectivenessRuleServiceApi(currentConfig);
export const mediaService = new MediaServiceApi(currentConfig);
export const leadService = new LeadServiceApi(currentConfig);
export const alertService = new AlertServiceApi(currentConfig);
export const dashboardService = new DashboardServiceApi(currentConfig);
export const leadStatusService = new LeadStatusServiceApi(currentConfig);
export const chartService = new ChartServiceApi(currentConfig);
export const reportService = new ReportServiceApi(currentConfig);
export const scheduledReportService = new ScheduledReportServiceApi(
  currentConfig,
);
export const leadScoreAdjustmentService = new LeadScoreAdjustmentRuleServiceApi(
  currentConfig,
);
export const APITokenService = new APITokenServiceApi(currentConfig);
export const CSVService = new CSVServiceApi(currentConfig);
export const productService = new ProductServiceApi(currentConfig);
export const syncService = new SyncServiceApi(currentConfig);
export const pageviewMetaService = new PageviewMetaServiceApi(currentConfig);
export const attributionTagService = new AttributionTagsServiceApi(
  currentConfig,
);
export const jobService = new JobServiceApi(currentConfig);
export const analyticsService = new AnalyticsServiceApi(currentConfig);
export const personalisationService = new PersonalisationServiceApi(
  currentConfig,
);
export const landingDomainService = new LandingDomainServiceApi(currentConfig);
export const landersService = new LandersServiceApi(currentConfig);
export const utmService = new UTMServiceApi(currentConfig);
export const sessionService = new SessionServiceApi(currentConfig);
export const subscriptionService = new SubscriptionServiceApi(currentConfig);
export const sourceCategoryService = new SourceCategoryServiceApi(
  currentConfig,
);
export const analyticsLabelService = new LabelServiceApi(currentConfig);
export const onboardingService = new OnboardingServiceApi(currentConfig);
export const monitoringService = new MonitoringServiceApi(currentConfig);
export const aiService = new AiServiceApi(currentConfig);
export const lastRunInfoService = new LastRunInfoServiceApi(currentConfig);
export const oAuthService = new OAuthServiceApi(currentConfig);
export const samlService = new SamlServiceApi(currentConfig);
export const incrementalityService = new IncrementalityServiceApi(currentConfig);

// This is old service which will change soon
export const customisationService = new CustomisationApi(currentConfig);
